import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { Alert, Button, CollapseWithIcon, type IColumn, Table } from 'shared/ui'
import { type Integration } from 'entities/Integrations'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useIntegrationStoreMemo } from 'pages/settings/pages/integrations/pages/integrationSettings/hooks/useIntegrationStoreMemo'
import { useIntegrationDetailsContext } from 'pages/settings/pages/integrations/context/integrationDetailsContext'
import {
  IntegrationGoogleCalendarSettingsStore,
  type IGoogleCalendarAccount,
} from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationGoogleCalendarSettings/store/IntegrationGoogleCalendarSettingsStore'
import {
  GoogleCalendarAccountCell,
  GoogleCalendarAccountMappingCell,
} from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationGoogleCalendarSettings/ui/GoogleCalendarAccountRow/GoogleCalendarAccountRow'
import sharedStyles from 'pages/settings/pages/integrations/pages/integrationSettings/ui/styles.module.scss'
import styles from './styles.module.scss'

type IIntegrationGoogleCalendarSettings = {
  integration: Integration
}

export const IntegrationGoogleCalendarSettings = observer(
  ({ integration }: IIntegrationGoogleCalendarSettings) => {
    const entities = useEntitiesContext()
    const { onConnect, isManageConnectionProcessing } = useIntegrationDetailsContext()
    const store = useIntegrationStoreMemo(
      IntegrationGoogleCalendarSettingsStore,
      integration,
      entities
    )
    const {
      accounts,
      accountsCount,
      mappedAccountsCount,
      organizationMembers,
      opened,
      onToggle,
      disconnectingAccountId,
      total,
      pagination,
      onChangePagination,
      fetchAccounts,
    } = store

    const handleAddAccount = async () => {
      await onConnect()
      await fetchAccounts()
    }

    const columns: IColumn<IGoogleCalendarAccount>[] = useMemo(
      () => [
        {
          field: 'account',
          name: 'Google account calendar',
          width: '280px',
          renderRowCell: (row) => <GoogleCalendarAccountCell account={row} />,
        },
        {
          field: 'select',
          name: 'Salesmsg user',
          renderRowCell: (row) => (
            <GoogleCalendarAccountMappingCell
              account={row}
              members={organizationMembers}
              onUserChange={store.onUserChange}
              onDisconnect={store.onDisconnect}
              disconnectLoading={disconnectingAccountId === row.id}
            />
          ),
        },
      ],
      [organizationMembers, store.onUserChange, store.onDisconnect, disconnectingAccountId]
    )

    return (
      <CollapseWithIcon
        opened={opened}
        onClick={onToggle}
        icon={'userLine'}
        title={'Accounts'}
        subtitle={`(${mappedAccountsCount} / ${accountsCount})`}
        label={'Map Google account calendars to Salesmsg users'}
        headerClassName={sharedStyles.collapseWithIcon}
        className={styles.collapseContent}
      >
        <div>
          <Alert
            item={{
              type: 'infoBlue',
              desc: 'Team members can also connect their own calendar from Profile settings. It appears here automatically, already mapped to them.',
            }}
            className={styles.alert}
          />

          {!!accounts.length && (
            <div className={styles.table}>
              <Table
                ariaLabel={'GoogleCalendarAccounts'}
                columns={columns}
                rows={accounts}
                rowsCount={total}
                startPaginationSize={10}
                initPage={pagination.page}
                limit={pagination.limit}
                onChangePagination={onChangePagination}
                virtualization
                scrollbarProps={{
                  autoHeight: true,
                  autoHeightMin: 0,
                  autoHeightMax: 610,
                  viewClassName: styles.viewScrollClassName,
                }}
              />
            </div>
          )}

          <Button
            text={'Add account'}
            icon={'add'}
            typeBtn={'contained'}
            contained={'secondary'}
            className={styles.addButton}
            onClick={handleAddAccount}
            loading={isManageConnectionProcessing}
          />
        </div>
      </CollapseWithIcon>
    )
  }
)
